// Central content model for the Titan Haven Group corporate site.
// Products, divisions, news, and evidence are data-driven so new entries
// can be added later without redesigning pages.
//
// Items flagged assetStatus: "pending" need real screenshots/imagery
// from the operator before publication. Do not replace with fake visuals.

export const CONTACT = {
    email: "operations@titanhavengroup.com",
    phone: "(307) 204-4095",
    phoneHref: "tel:+13072044095",
};

export const NAV = [
    { label: "Company", to: "/company", testId: "nav-company" },
    { label: "Portfolio", to: "/portfolio", testId: "nav-portfolio" },
    { label: "WarRoom", to: "/warroom", testId: "nav-warroom" },
    { label: "Government & Research", to: "/government-research", testId: "nav-government-research" },
    { label: "News & Insights", to: "/news", testId: "nav-news" },
    { label: "Pricing & Engagement", to: "/pricing", testId: "nav-pricing" },
    { label: "Contact", to: "/contact", testId: "nav-contact" },
];

export const METHOD = [
    {
        step: "01",
        name: "Observe",
        text: "Identify what is actually happening — in a market, a business, an operating environment, or a customer's behavior.",
    },
    {
        step: "02",
        name: "Question",
        text: "Challenge assumptions, conventional explanations, existing processes, and accepted ways of doing things.",
    },
    {
        step: "03",
        name: "Synthesize",
        text: "Combine operating knowledge, research, technology, and observation into a workable answer.",
    },
    {
        step: "04",
        name: "Act",
        text: "Build, deploy, acquire, operate, or test — turn the conclusion into something real.",
    },
];

// Maturity hierarchy — controls visual weight across the site.
// 1 = Built / Operating / Commercial (substantial presentation)
// 2 = Active / Partially Built (moderate presentation)
// 3 = Upcoming / Exploratory (brief treatment)
// 4 = Structural / Future Vehicles (one or two lines)
// Portfolio data model. Every entry can carry:
// name (display), legalName, category/division, status (maturity),
// relationship (ownership/affiliation — prevents inaccurate public claims),
// summary, destination (slug route or externalUrl), assetStatus/evidence, visibility.
export const RELATIONSHIP = {
    OWNED: "THG-owned",
    AFFILIATE: "THG-affiliated entity",
    INTEREST: "Ownership/economic interest",
    WITHIN: "Developed within another entity",
};

export const PRODUCTS = [
    {
        slug: "warroom-storecentral",
        name: "WarRoom StoreCentral",
        legalName: null,
        division: "WarRoom Solutions",
        category: "Commercial Software",
        status: "Commercial Product",
        relationship: RELATIONSHIP.OWNED,
        visibility: "featured",
        level: 1,
        summary:
            "A commercially available software product from WarRoom Solutions, built for retail store operations.",
        externalUrl: "https://storecentral.titanhavengroup.com",
        image: "/assets/products/storecentral-today.jpg", // Authentic capture from the running StoreCentral product (demo tenant).
        assetStatus: "supplied", // Replace placeholder frame with real product screenshot when supplied.
    },
    {
        slug: "control-shift",
        name: "Control Shift",
        aka: "Manager Control",
        legalName: null,
        division: "WarRoom Solutions",
        category: "Commercial Software",
        status: "Commercial Product",
        relationship: RELATIONSHIP.OWNED,
        visibility: "featured",
        level: 1,
        summary:
            "A commercially available software product from WarRoom Solutions — a mobile-first operating layer for retail store managers, also referred to as Manager Control.",
        externalUrl: "https://controlshift.titanhavengroup.com",
        image: "/assets/products/control-shift-screenshot.png", // Real product screenshot preserved from the previous THG site.
        assetStatus: "supplied",
    },
    {
        slug: "hoteltrips",
        name: "HotelsTrips.com",
        legalName: null,
        division: "Digital Businesses",
        category: "Digital Business",
        status: "Operating Digital Business",
        relationship: RELATIONSHIP.OWNED,
        visibility: "featured",
        level: 1,
        summary: "A travel digital property. Rebuilt and currently operating.",
        externalUrl: "https://hotelstrips.com",
        image: "/assets/products/hoteltrips-screenshot.jpg", // Real capture of the live operating site, taken 2026-08-30.
        assetStatus: "supplied",
    },
    {
        slug: "warroom-ai",
        name: "Sarge",
        aka: "WarRoom AI",
        legalName: null,
        division: "WarRoom Solutions",
        category: "Commercial Software",
        status: "Live",
        relationship: RELATIONSHIP.OWNED,
        visibility: "featured",
        level: 2,
        summary:
            "An AI COO for operating founders — the WarRoom AI product from WarRoom Solutions, launched and live at sarge.titanhavengroup.com.",
        externalUrl: "https://sarge.titanhavengroup.com",
        image: "/assets/products/warroom-ai-hero.jpg", // Authentic capture of the live WarRoom AI public product site.
        assetStatus: "supplied",
    },
];

export const ACTIVE_INITIATIVES = [
    {
        name: "C.A.R.T.-Right Fund",
        division: "Titan Haven Group",
        status: "In Development",
        level: 3,
        summary:
            "A new initiative being developed by Titan Haven Group. Additional details will be announced at a later date.",
    },
    {
        name: "Bud's Market Intelligence Desk",
        division: "WarRoom Labs",
        status: "Live",
        level: 2,
        summary:
            "An experimental WarRoom Labs market-intelligence desk — the Bud Basket v2.6, ranked securities research on a defined review cycle — live at bud.titanhavengroup.com.",
    },
    {
        name: "THLS AI",
        division: "Government & Research",
        status: "Active Development",
        level: 2,
        summary:
            "A workplace communication readiness platform under active SBIR/STTR development.",
    },
    {
        name: "Sports / Media / IP",
        division: "Media & IP",
        status: "In Development",
        level: 2,
        summary:
            "Media and IP interests and initiatives in development, including Yakkers Huddle Media Group, Inc. (Sports Pod Time Machine) and Broken Circuit Records. Turf Talk — owned and operated by SportChant, LTD. — is a working closed beta presented separately above.",
    },
];

export const DIVISIONS = [
    {
        name: "WarRoom Solutions",
        text: "Commercial software and operating technology.",
        to: "/warroom",
        testId: "division-warroom-solutions",
    },
    {
        name: "WarRoom Labs",
        text: "Emerging technology and applied research. Publishes the Operational Intelligence Brief.",
        to: "/warroom",
        testId: "division-warroom-labs",
    },
    {
        name: "THG Retail Concepts",
        text: "Physical products and retail concepts — PBLC, Hat Bar, headwear, and apparel.",
        to: "/portfolio/retail",
        external: false,
        destination: "https://thgretail.com", // Permanent external destination. Shopify storefront is temporarily offline while the site is rebuilt (new storefront frontend + Shopify commerce backend). Set linkActive: true to restore external routing — no redesign needed.
        linkActive: false,
        testId: "division-retail",
        statusNote: "Site Under Reconstruction",
    },
    {
        name: "THGov",
        text: "Government-facing capability and public-sector proposals.",
        to: "/government-research",
        testId: "division-thgov",
    },
    {
        name: "Sports / Media / IP",
        text: "Sports media and intellectual property interests and initiatives.",
        to: "/portfolio/media",
        testId: "division-media",
    },
    {
        name: "Digital Businesses",
        text: "Owned and operated digital properties, including HotelsTrips.com.",
        to: "/portfolio#digital",
        testId: "division-digital",
    },
];

export const EVIDENCE = [
    {
        type: "Credential",
        title: "Pennsylvania ITQ Approved Vendor",
        text: "Approved vendor — Management & Government Operations Analysis.",
    },
    {
        type: "Published Research",
        title: "Operational Intelligence Brief",
        text: "Research published through WarRoom Solutions.",
        href: "https://warroomlabs.substack.com/",
    },
    {
        type: "Products in Market",
        title: "StoreCentral & Control Shift",
        text: "Commercially available software products from WarRoom Solutions.",
    },
    {
        type: "Operating Business",
        title: "HotelsTrips.com",
        text: "Digital travel property — rebuilt and in operation.",
        href: "https://hotelstrips.com",
    },
    {
        type: "Selected External Work",
        title: "Commercial Reality Diagnostic",
        text: "Redacted diagnostic for a multi-property commerce portfolio.",
        href: "/assets/resources/THG_WarRoom_Redacted_Conversion_Intent_Diagnostic_Case_Study.pdf",
    },
    {
        type: "Selected External Work",
        title: "Strategic Messaging & Governance",
        text: "Confidential engagement — a reusable governance system for organizational communication.",
        href: "/assets/resources/THG_Confidential_Strategic_Messaging_Governance_Case_Brief.pdf",
    },
];

// Real dated items migrated from the previous site's Press page.
// body blocks: string = paragraph; {h} = subheading; {q, by} = quotation.
export const PRESS = [
    {
        slug: "bud-market-intelligence-desk-launch",
        type: "Press Release",
        date: "September 8, 2026",
        title: "Titan Haven Group Launches Bud's Market Intelligence Desk",
        text: "Market intelligence that keeps receipts.",
        metaTitle: "Bud's Market Intelligence Desk Launch — WarRoom Labs | Titan Haven Group LLC",
        metaDescription:
            "Archived September 8, 2026 launch release. Bud's current launch product is the Bud Basket v2.6 — ranked securities research on a defined review cycle; the directional calls described in this release were not adopted for launch and are preserved as historical/experimental research.",
        update: {
            date: "October 22, 2026",
            text: "Bud's launch product is the Bud Basket v2.6 — ranked securities research on a defined review cycle. The directional market calls described in this release were not adopted for launch and are preserved as historical/experimental research. See bud.titanhavengroup.com for current product status.",
        },
        body: [
            "Sheridan, Wyoming — September 8, 2026. Titan Haven Group LLC today announced the launch of Bud's Market Intelligence Desk, a market-research and market-intelligence product developed by WarRoom Labs, now live at bud.titanhavengroup.com.",
            "Bud's Market Intelligence Desk is built on a simple standard: market intelligence that keeps receipts. Bud works from live market data and publishes market-intelligence calls that state their confidence and expected ranges up front. Every published call is timestamped and later graded under the disclosed method; wins and losses remain on the public record, so anyone can see how prior calls performed.",
            "The desk operates in the open. Its public surfaces include the Open Desk, a Morning Brief, published methodology, Calls, a Scorecard and Compare, and anyone can follow the desk or register free. The public record is building now; Titan Haven Group claims no established predictive edge and quotes no performance figures — the record is left to speak for itself.",
            {
                q: "Most market commentary asks to be trusted and is never heard from again. Bud was built so it never has to ask. Every call carries its confidence and its range, and every result stays on the record — including the ones we would rather forget. If the desk is wrong, the receipts say so.",
                by: "Peter Saad, Managing Member, Titan Haven Group LLC",
            },
            "Bud's Market Intelligence Desk is market research and market intelligence for informational and educational purposes. It is not personalized investment advice, and it does not take into account any person's objectives, financial situation or needs.",
            "Bud's Market Intelligence Desk is developed and operated by WarRoom Labs, the emerging-technology and applied-research division of Titan Haven Group LLC, which also publishes the Operational Intelligence Brief.",
            {
                p: "Product website:",
                link: { text: "Bud's Market Intelligence Desk — bud.titanhavengroup.com", href: "https://bud.titanhavengroup.com" },
            },
            { h: "About WarRoom Labs" },
            "WarRoom Labs is the emerging-technology and applied-research division of Titan Haven Group LLC. It publishes the Operational Intelligence Brief.",
            { h: "About Titan Haven Group" },
            "Titan Haven Group LLC builds and operates software, emerging technology, consumer businesses, digital assets, and media/IP. Observe. Question. Synthesize. Act.",
        ],
    },
    {
        slug: "turftalk-controlled-demonstrations",
        type: "Press Release",
        date: "September 4, 2026",
        title: "SportChant Opens Controlled Demonstrations of the Rebuilt Turf Talk Football Experience",
        text: "The closed-beta product brings game-day conversation, predictions, rivalry leagues and original character programming into one experience",
        metaTitle: "Turf Talk Controlled Demonstrations — SportChant, LTD. | Titan Haven Group LLC",
        metaDescription:
            "SportChant, LTD. opens controlled demonstrations of the rebuilt Turf Talk closed beta — a football second-screen experience designed and built with Titan Haven Group.",
        body: [
            "SHERIDAN, Wyoming — September 4, 2026 — Titan Haven Group LLC today announced that SportChant, LTD., a New York corporation, is opening controlled demonstrations of the rebuilt Turf Talk closed beta, a football second-screen experience owned and operated by SportChant, LTD. and designed and built with Titan Haven Group.",
            "Turf Talk brings NFL and college-football game views, matchup-specific conversation, picks and predictions, rivalry leagues, tailgate planning and the original Turf Talk Character Show into one product. The Character Show features original SportChant characters, including Alpha Moose, Cow Pie and Bruce, delivering voiced and animated programming around selected matchups.",
            "The rebuilt product includes mobile and desktop experiences. Its working closed-beta environment currently uses clearly identified demonstration data while the live sports-data connection is completed. The public Turf Talk website serves as the product's front door; the application interior, seeded demonstrations and participant features are available only through controlled access.",
            {
                q: "Turf Talk started with a simple idea: watching football is better when you have people to talk to, argue with and make picks with while the game is happening. We rebuilt the product so the scoreboard, the conversation, the predictions and the Character Show live in one experience. We are keeping access controlled while we finish the live-data connection because we want people to see the product working—not hand them another promise.",
                by: "Peter Saad, Managing Member, Titan Haven Group LLC",
            },
            "Prospective partners, investors, beta participants and media may request a guided demonstration through Titan Haven Group. No public demonstration credentials are posted, and access to the product interior is not granted through the public website.",
            {
                imgs: [
                    {
                        src: "/assets/products/turf-talk-phone-home.jpg",
                        alt: "Turf Talk phone app — opening screen with the day's game slate, free scoreboard preview",
                    },
                    {
                        src: "/assets/products/turf-talk-phone-slate.jpg",
                        alt: "Turf Talk phone app — game slate showing final, halftime, delayed and live states, each with a room",
                    },
                    {
                        src: "/assets/products/turf-talk-phone-game.jpg",
                        alt: "Turf Talk phone app — matchup view for Oklahoma at Texas with room, stats and prediction-card entry",
                    },
                ],
                caption:
                    "The rebuilt Turf Talk mobile experience in the closed-beta environment. Displayed games and data are seeded for demonstration — not a live sports feed.",
            },
            {
                p: "Product website:",
                link: { text: "Turf Talk — turftalk.titanhavengroup.com", href: "https://turftalk.titanhavengroup.com" },
            },
            "Request a demonstration: operations@titanhavengroup.com",
            { h: "About SportChant, LTD." },
            "SportChant, LTD. is a New York corporation that owns and operates Turf Talk, a football conversation and prediction experience built around the social side of watching the game.",
            { h: "About Titan Haven Group LLC" },
            "Titan Haven Group LLC is a Wyoming-based operating and development company that builds software, emerging technology, consumer businesses, digital assets and media/IP. Titan Haven Group designed and built the rebuilt Turf Talk experience with SportChant.",
        ],
    },
    {
        slug: "warroom-storecentral-launch",
        type: "Press Release",
        date: "September 4, 2026",
        title: "Titan Haven Group Launches WarRoom StoreCentral for Multi-Store Retail Operations",
        text: "Operator-built platform unifies store execution, communication, workforce management, forecasting, learning and field oversight in one configurable system.",
        metaTitle: "WarRoom StoreCentral Launch — Retail Operations Platform | Titan Haven Group LLC",
        metaDescription:
            "Titan Haven Group launches WarRoom StoreCentral, the WarRoom Solutions platform unifying store execution, workforce management, forecasting, learning and field oversight.",
        body: [
            "Sheridan, Wyoming — September 4, 2026. Titan Haven Group LLC today announced the launch of WarRoom StoreCentral, an enterprise retail operations and execution platform developed by WarRoom Solutions. StoreCentral connects the corporate operating plan to daily store-floor execution across regions, districts and individual locations.",
            "The platform brings together shift command, store communications, task execution, forms and evidence, knowledge management, learning and certification, workforce scheduling, time and attendance, labor planning and forecasting, store visits and audits, and performance oversight. StoreCentral includes Shift Command, its native operating-day workflow for identifying what needs attention now, what changed during the day and what must be handed off to the next shift.",
            {
                q: "StoreCentral was built around what actually happens inside stores, not just what appears on a dashboard. Retailers already have systems that tell them what happened. What they often lack is a way to see what is changing, understand why the plan no longer fits, and give managers and frontline teams a clear next move while there is still time to act. StoreCentral closes that gap.",
                by: "Peter Saad, Managing Member, Titan Haven Group LLC",
            },
            "StoreCentral was designed around a basic operational reality: conditions inside a store can change faster than a conventional forecast. Authorized company leaders can revise projections when documented factors—such as technology failures, unusual customer-flow conditions or other operating disruptions—make the original forecast unreliable. The original forecast remains preserved, the adjustment is auditable, and affected teams, including frontline employees, can be notified of the change.",
            "The platform supports the tenant, region, district and store hierarchy used by multi-location retailers, with role-based access, configurable modules and isolated customer data. Its user-facing experience is available in both English and Spanish so store teams can operate in the language that best supports execution.",
            "WarRoom StoreCentral is being introduced as a sales-led enterprise offering rather than a public self-service subscription. Retail organizations can request a demonstration and work with Titan Haven Group to define a pilot, configuration and rollout appropriate to their operating structure.",
            {
                p: "Product website:",
                link: { text: "WarRoom StoreCentral — storecentral.titanhavengroup.com", href: "https://storecentral.titanhavengroup.com" },
            },
            { h: "About Titan Haven Group" },
            "Titan Haven Group LLC builds and operates software, emerging technology, consumer businesses, digital assets, and media/IP. Observe. Question. Synthesize. Act.",
        ],
    },
    {
        slug: "sarge-founding-beta",
        type: "Press Release",
        date: "September 3, 2026",
        title: "WarRoom Solutions opens Founding Beta for Sarge, an AI COO built for operating founders",
        text: "WarRoom AI gives founders a continuing operating system for daily priorities, business memory, commitments, decisions and follow-through.",
        href: "https://sarge.titanhavengroup.com",
        linkLabel: "Sarge — Your WarRoom Confidant",
        metaTitle: "Sarge Founding Beta — AI COO for Founders | Titan Haven Group LLC",
        metaDescription:
            "WarRoom Solutions opens the Sarge Founding Beta, a persistent AI COO built to help operating founders preserve context, set priorities and follow through.",
        body: [
            "Sheridan, Wyoming — September 3, 2026. Titan Haven Group LLC announced that the Founding Beta of WarRoom AI — Sarge is now available on the web at sarge.titanhavengroup.com.",
            "Developed by WarRoom Solutions, Sarge is an AI chief operating officer designed for founders managing real businesses with limited time, limited capacity and too many competing demands. It is built to create operating discipline, preserve business context, identify what needs attention now and carry decisions, commitments and outcomes forward.",
            "Founders rarely suffer from a shortage of ideas, documents, dashboards or task lists. The operating problem is that critical context becomes scattered, urgent work crowds out important work, difficult decisions remain open, commitments slip without consequence and the lessons behind prior choices disappear over time. Sarge is designed around that reality.",
            {
                q: "We did not build Sarge to give founders another place to chat with AI. We built it to create operating discipline. Sarge learns the business, tells the founder what needs attention now, and keeps important decisions and commitments from disappearing into the noise.",
                by: "Peter Saad, Managing Member, Titan Haven Group LLC",
            },
            { h: "A continuing command cycle for the founder" },
            "Sarge operates around a closed-loop method:",
            "Situation → Priority → Commitment → Follow-Through → Outcome → Learning",
            "Sarge learns the company through a conversational operating intake, preserves confirmed business facts with source history, reviews priorities and constraints, recommends the next move that matters most and connects that recommendation to the commitments and outcomes that follow. The founder remains in command: recommendations can be accepted, modified or rejected, and proposed actions remain subject to explicit approval.",
            { h: "An operating workspace, not merely an assistant" },
            "The Founding Beta includes the Daily Command Cycle, natural business memory, grounded Ask Sarge conversations, a Command Board for priorities and commitments, a Decision Journal, Weekly Operating Review, Financial Pulse, business-file analysis and document creation.",
            "Sarge is not positioned as an autonomous executive and does not replace accountable human judgment. It does not silently send communications, make financial commitments or take control of external business systems. Its purpose is to improve operating clarity, continuity and follow-through while making uncertainty visible.",
            { h: "Founding Beta availability" },
            "The Sarge Founding Beta is initially limited to 10 WarRooms. Founding access is $149 per month or $1,490 for one year paid upfront. Each subscription includes one founder, one business workspace and up to 500 model requests per month.",
            "Founders can learn more and request access at https://sarge.titanhavengroup.com.",
            {
                p: "Product website:",
                link: { text: "Sarge — sarge.titanhavengroup.com", href: "https://sarge.titanhavengroup.com" },
            },
        ],
    },
    {
        slug: "hoteltrips-relaunch",
        type: "Press Release",
        date: "August 29, 2026",
        title: "HotelsTrips.com relaunched following full platform rebuild",
        text: "The rebuilt travel platform returns to active operation as a THG digital-commerce property.",
        href: "https://hotelstrips.com",
        body: [
            "Sheridan, Wyoming — August 29, 2026. Titan Haven Group LLC announced the relaunch of HotelsTrips.com following a full rebuild of the travel platform and its return to active operation.",
            "HotelsTrips.com is an established digital travel property within Titan Haven Group's portfolio. The rebuilt platform provides travelers with access to hotel and travel-booking options while restoring the property as an active digital commerce asset.",
            "The relaunch follows Titan Haven Group's evaluation of the existing HotelsTrips property and a decision to rebuild the platform rather than leave the underlying digital asset dormant. The new site establishes a modern operating foundation for HotelsTrips and provides THG with the ability to continue developing the property as travel technology, commerce opportunities, and user needs evolve.",
            {
                q: "HotelsTrips already existed as an asset, but an asset only has value if you do something with it. We looked at what we had, questioned what the property should become, rebuilt it, and put it back into operation. That is increasingly how we approach opportunities across Titan Haven Group.",
                by: "Peter Saad, Managing Member, Titan Haven Group LLC",
            },
            "The project reflects Titan Haven Group's broader operating philosophy: Observe. Question. Synthesize. Act. THG applies that approach across technology, commerce, retail, media and intellectual property, research, and institutional initiatives.",
            "HotelsTrips.com is now live and accessible to travelers, and will continue to be developed as an active travel and digital-commerce property within the Titan Haven Group portfolio.",
            {
                p: "Product website:",
                link: { text: "HotelsTrips.com — hotelstrips.com", href: "https://hotelstrips.com" },
            },
        ],
    },
    {
        slug: "manager-control-intro",
        type: "Press Release",
        date: "August 23, 2026",
        title: "WarRoom Solutions introduces Manager Control for live store execution",
        text: "A mobile-first operating layer for retail store managers. WarRoom Solutions is opening discussions with retail operators on a limited pilot program.",
        body: [
            "Sheridan, Wyoming — August 23, 2026. Titan Haven Group LLC announced the introduction of Manager Control, a new WarRoom Solutions product designed to give retail store managers a structured way to establish their operating position, capture changing floor conditions, manage interventions and preserve the context surrounding store performance.",
            "Retail organizations already generate significant amounts of performance data through point-of-sale, workforce management, scheduling, task-management and analytics systems. Manager Control is designed to address a different part of store operations: what happens between the information those systems provide and the decisions managers make while actively running the floor.",
            {
                q: "Store results tell leadership what happened, but they do not always preserve what was happening inside the store while those results were being produced. Manager Control is designed to capture that operating context at the source, while the manager can still do something about it.",
                by: "Peter Saad, Managing Member, Titan Haven Group LLC",
            },
            { h: "From performance data to operating context" },
            "Manager Control allows store managers to establish a primary outcome, identify the principal constraint affecting the shift and set immediate operating priorities. As conditions change, managers can record operational observations, availability gaps, missed demand and management actions. KPI checks place performance measures alongside contemporaneous operating conditions and document what action remains within management's control. The system does not automatically assign causation between an operating condition and a performance result — it preserves the information necessary to interpret results with greater operational context.",
            { h: "A continuous operating record" },
            "Manager Control is structured around the operating day rather than a retrospective reporting cycle. Opening Control establishes the initial operating position. Midshift Reset allows that position to be deliberately reassessed as conditions change. KPI Check connects performance measures with contemporaneous floor conditions and management response. Closing Handoff preserves unresolved issues and operating context for continuity beyond the individual shift. District and operational leadership can view that record without taking control of day-to-day floor decisions away from store management.",
            { h: "Designed to complement existing retail systems" },
            "Manager Control is not intended to replace point-of-sale, workforce management, scheduling, payroll, human resources, task-management or enterprise analytics platforms. It sits between those systems and live store execution — turning existing inputs into an operating position and preserving what changes, what management does in response, and what leadership may need to understand later. The initial version is intentionally focused on frontline operating control and does not use generative AI, employee scoring, forecasting or gamification.",
            { h: "Limited retail pilot program" },
            "WarRoom Solutions is beginning discussions with retail operators regarding a limited Manager Control pilot program. The pilot is designed to deploy Manager Control in a small number of stores during normal operations and evaluate whether the operating record improves manager control, shift continuity and leadership understanding of store conditions — under actual store operating conditions, not a simulated environment. Retail operators interested in discussing pilot participation can contact Titan Haven Group LLC directly.",
            {
                p: "Product website:",
                link: { text: "Control Shift — controlshift.titanhavengroup.com", href: "https://controlshift.titanhavengroup.com" },
            },
        ],
    },
    {
        slug: "thls-ai-advances",
        type: "Press Release",
        date: "June 25, 2026",
        title: "THLS AI workplace communication readiness system advances",
        text: "Continued development for adult English learners, workforce programs, and institutional training environments.",
        body: [
            "Sheridan, Wyoming — June 25, 2026. Titan Haven Group LLC continues development of THLS AI, an AI-assisted workplace communication readiness system designed to support adult English learners, workforce programs, and institutional training environments.",
            "THLS AI is being developed as part of Titan Haven Group's broader AI systems and institutional infrastructure initiatives. The system is designed to support structured workplace communication practice, scenario-based learning, adaptive feedback, learner confidence tracking, and readiness-oriented progress visibility.",
            "Unlike general-purpose chatbot tools, THLS AI is being designed around controlled workplace scenarios, communication-readiness objectives, instructor and program visibility, and practical implementation needs for adult learning and workforce environments.",
            "Development efforts have included engagement with adult education, workforce readiness, and professional association stakeholders to better understand implementation needs and communication challenges faced by adult learners preparing for workplace environments.",
            "Titan Haven Group will continue advancing THLS AI through product development, institutional outreach, pilot planning, and applied research related to workplace communication readiness.",
        ],
    },
    {
        slug: "dashboard-induced-distortion",
        type: "Research",
        date: "June 2026",
        title: "WarRoom Labs introduces the Dashboard-Induced Distortion framework",
        text: "Operational intelligence research on how organizations can lose visibility into operational reality as reporting and dashboards expand.",
        href: "https://warroomlabs.substack.com/",
        body: [
            "Sheridan, Wyoming — June 2026. WarRoom Labs, the research and analytical division of Titan Haven Group LLC, announced the introduction of the Dashboard-Induced Distortion framework, an operational intelligence concept examining how organizations can gradually lose visibility into operational reality despite increasing access to data, reporting systems, dashboards, and performance metrics.",
            "The framework explores how information is compressed as it moves upward through organizational hierarchies. Reports, dashboards, scorecards, forecasts, and executive summaries improve visibility and efficiency, but can also reduce context and operational meaning over time.",
            "Dashboard-Induced Distortion does not suggest that metrics or dashboards are inaccurate. Rather, the framework examines how organizations can become increasingly dependent on indicators while becoming further removed from the underlying conditions those indicators attempt to describe.",
            {
                q: "The dashboard is not wrong. The challenge is that operational reality is always larger than the dashboard. Organizations often receive more information than ever before while understanding less about what is actually happening inside day-to-day operations.",
                by: "Peter Saad, Managing Member, Titan Haven Group LLC",
            },
            "The concept forms part of WarRoom Labs' broader Operational Intelligence research initiative, which focuses on execution visibility, decision architecture, organizational adaptation, information flow, behavioral incentives, and institutional reliability.",
        ],
    },
    {
        slug: "sports-pod-time-machine",
        type: "Press Release",
        date: "June 2026",
        title: "Sports Pod Time Machine joins THG's Media & IP portfolio",
        text: "The property comes under the portfolio through THG's control of Yakkers Huddle Media Group, Inc., a New York corporation. No relaunch date announced.",
        body: [
            "Sheridan, Wyoming — June 2026. Titan Haven Group LLC announced that The Sports Pod Time Machine has been formally brought under Titan Haven Group's Media & IP portfolio through THG's control of Yakkers Huddle Media Group, Inc., a New York corporation.",
            "The Sports Pod Time Machine is a sports-media property focused on revisiting sports history, legacy moments, cultural memory, debates, personalities, and historical sports narratives through a modern media format. Under Titan Haven Group, the property will be evaluated for future redevelopment, relaunch, and integration into THG's broader media and IP structure.",
            {
                q: "Titan Haven Group is being built as a long-horizon operating platform. Bringing The Sports Pod Time Machine under THG gives the company a sports-media asset with history, flexibility, and future development potential.",
                by: "Peter Saad, Managing Member, Titan Haven Group LLC",
            },
            "Titan Haven Group does not currently announce a formal relaunch date for The Sports Pod Time Machine. The property remains part of the company's active Media & IP portfolio and may be developed alongside other THG media, content, and intellectual property projects.",
            "Yakkers Huddle Media Group, Inc. is a New York corporation associated with sports-media development and the creation of media properties including The Sports Pod Time Machine.",
        ],
    },
    {
        slug: "thgov-expansion",
        type: "Press Release",
        date: "June 2026",
        title: "THGov expands public-sector support activities",
        text: "Government, education, nonprofit, and institutional support across proposal development, research synthesis, documentation, and operational analysis.",
        body: [
            "Sheridan, Wyoming — June 2026. Titan Haven Group LLC announced the continued expansion of THGov, the company's public-sector support division focused on government, educational, nonprofit, authority, and institutional organizations.",
            "THGov was established to support organizations requiring research, proposal development, documentation support, operational analysis, strategic communications assistance, executive-ready deliverables, and administrative project support.",
            "The company continues to pursue opportunities across municipal, educational, workforce development, community development, public-sector modernization, and institutional support environments. THGov's activities currently include procurement participation, proposal development, market analysis, operational assessment, and research synthesis support.",
            {
                q: "THGov was built around a simple idea. Organizations often need practical analysis, research support, documentation assistance, and operational clarity without adding unnecessary complexity. We believe there is significant value in helping institutions make better use of information they already possess.",
                by: "Peter Saad, Managing Member, Titan Haven Group LLC",
            },
            "The continued development of THGov reflects the company's broader strategy of building long-horizon capabilities across operational intelligence, AI systems, institutional support, research, public-sector engagement, and strategic infrastructure.",
        ],
    },
    {
        slug: "ecosystem-expansion",
        type: "Press Release",
        date: "June 2026",
        title: "THG announces public expansion of its operating ecosystem",
        text: "Operational intelligence, AI systems, public-sector support, retail concepts, media/IP, research, and institutional infrastructure under one structure.",
        body: [
            "Sheridan, Wyoming — June 2026. Titan Haven Group LLC announced the public expansion of its operating ecosystem, bringing together work across operational intelligence, AI systems, public-sector support, retail concepts, media and intellectual property, research, and institutional infrastructure.",
            "The company's current structure includes WarRoom Solutions, WarRoom Labs, THLS AI, THGov, AI Systems, Retail Concepts, Media & IP, and Asset Strategy. WarRoom Solutions focuses on operational intelligence, KPI interpretation, dashboard distortion, execution visibility, and decision-layer clarity. THLS AI is focused on workplace communication readiness and AI-supported learning systems. THGov supports public-sector, education, nonprofit, and institutional organizations with proposal development, documentation support, research synthesis, operational analysis, and executive-ready deliverables.",
            "The company's broader ecosystem also includes THGRetail.com, Hotelstrips.com, WarRoom Labs, and media/IP assets connected to long-horizon development.",
            {
                q: "Titan Haven Group was built to connect strategy, operations, technology, research, and execution into one operating structure. The public site now reflects the broader architecture of the company and gives clients, partners, reviewers, and collaborators a clear place to understand the work.",
                by: "Peter Saad, Managing Member, Titan Haven Group LLC",
            },
            "Titan Haven Group's current focus includes operational diagnostic work, AI-enabled workflow and communication systems, institutional support, public-sector opportunities, and long-term development of owned commerce, media, and intellectual property assets.",
        ],
    },
];

export const NEWS = PRESS;

// Sports / Media / IP entities. relationship fields prevent inaccurate public
// ownership claims — THG-owned products vs affiliated entities vs interests.
export const MEDIA_IP = [
    {
        name: "SportChant, LTD.",
        legalName: "SportChant, LTD.",
        category: "Sports / Media / IP",
        status: "Affiliated Entity",
        relationship: "THG-affiliated entity — not a wholly owned THG product",
        visibility: "moderate",
        summary: "New York corporation that owns and operates the Turf Talk football experience.",
        links: [],
    },
    {
        name: "Turf Talk",
        legalName: null,
        category: "Sports / Media / IP",
        status: "Closed Beta",
        relationship: "Owned and operated by SportChant, LTD. (a New York corporation); designed and built with Titan Haven Group LLC",
        visibility: "moderate",
        summary:
            "A football second-screen platform owned and operated by SportChant, LTD., a New York corporation — designed and built with Titan Haven Group LLC. Scoreboards, per-game rooms, picks and predictions, and original character-driven programming. In closed, invite-only beta.",
        links: [],
        to: "/portfolio/turf-talk-2",
    },
    {
        name: "Yakkers Huddle Media Group, Inc.",
        legalName: "Yakkers Huddle Media Group, Inc.",
        category: "Sports / Media / IP",
        status: "Affiliated Entity",
        relationship: "THG-affiliated New York corporation",
        visibility: "moderate",
        summary:
            "Sports-media development entity. The Sports Pod Time Machine sits within THG's Media & IP portfolio through THG's control of Yakkers Huddle Media Group, Inc.",
        links: [
            {
                href: "https://www.youtube.com/@thesportspodtimemachine1461",
                label: "YouTube",
                testId: "media-yakkers-link",
            },
        ],
    },
    {
        name: "Sports Pod Time Machine",
        legalName: null,
        category: "Sports / Media / IP",
        status: "Media Property",
        relationship: "Held through Yakkers Huddle Media Group, Inc.",
        visibility: "moderate",
        summary:
            "Sports-media property revisiting sports history, legacy moments, and historical narratives. Under evaluation for future redevelopment; no relaunch date announced.",
        links: [],
    },
    {
        name: "Broken Circuit Records",
        legalName: null,
        category: "Sports / Media / IP",
        status: "Media / IP Initiative",
        relationship: "THG-associated media/IP initiative",
        visibility: "moderate",
        summary: "Record label initiative within THG's media/IP holdings.",
        links: [
            {
                href: "https://blowncircuitrecords.bandcamp.com/",
                label: "Bandcamp",
                testId: "media-bcr-link",
            },
        ],
    },
];

// Investment / asset / structural initiatives — brief treatment by design.
export const STRUCTURAL = [
    {
        name: "SPVs",
        legalName: null,
        category: "Investment / Asset / Structural",
        status: "Structural",
        relationship: "Structural vehicles",
        visibility: "brief",
        summary:
            "Special-purpose vehicle structures used or developed where appropriate within THG's asset and acquisition architecture.",
    },
];

export const INQUIRIES = [
    {
        name: "Commercial Software",
        text: "StoreCentral, Control Shift, and WarRoom software.",
        subject: "Commercial Software Inquiry",
        testId: "inquiry-commercial-software",
    },
    {
        name: "Pilot / Product Inquiry",
        text: "Pilot programs and product evaluation.",
        subject: "Pilot / Product Inquiry",
        testId: "inquiry-pilot-product",
    },
    {
        name: "Operational Intelligence & Strategic Engagements",
        text: "Focused operational diagnostics, operating analysis, management, measurement, and decision-flow problems — governance and strategic problem-solving.",
        subject: "Operational Intelligence / Strategic Engagement Inquiry",
        testId: "inquiry-operational-intelligence",
    },
    {
        name: "Partnerships & Ventures",
        text: "Partnerships, ventures, and acquisition structures.",
        subject: "Partnership / Venture Inquiry",
        testId: "inquiry-partnerships",
    },
    {
        name: "Government Opportunities",
        text: "Public-sector proposals and government-facing work.",
        subject: "Government Opportunity",
        testId: "inquiry-government",
    },
    {
        name: "Media / Press",
        text: "Press, media, and interview requests.",
        subject: "Media / Press Inquiry",
        testId: "inquiry-media",
    },
    {
        name: "General Corporate Inquiry",
        text: "Everything else — route it here.",
        subject: "General Corporate Inquiry",
        testId: "inquiry-general",
    },
];

export const PRINCIPLES = [
    {
        name: "We work in systems.",
        text: "Everything is designed to reinforce the broader operating structure.",
    },
    {
        name: "Tech-supported, not tech-dependent.",
        text: "Technology supports operational clarity, scalability, and institutional resilience.",
    },
    {
        name: "Scaling follows infrastructure.",
        text: "Scalable systems are built intentionally through operational architecture and disciplined execution.",
    },
    {
        name: "AI supports judgment.",
        text: "THG uses AI to support human judgment, workflows, interpretation, and execution — not to replace judgment.",
    },
];

// Published WarRoom Labs research (Substack is the external publication/archive;
// the corporate site curates selected items only).
export const RESEARCH = [
    {
        title: "When the Dashboard Says Yes and the Floor Says No",
        topic: "Execution Visibility",
        href: "https://substack.com/home/post/p-201132153",
    },
    {
        title: "Dashboard Accuracy vs Operational Reality",
        topic: "Goodhart's Law in Operating Environments",
        href: "https://warroomlabs.substack.com/p/dashboard-accuracy-vs-operational",
    },
    {
        title: "KPI Reinforcement Loops and Organizational Signal Distortion",
        topic: "KPI Reinforcement Loops",
        href: "https://warroomlabs.substack.com/p/kpi-reinforcement-loops-and-organizational",
    },
    {
        title: "Operational Interruptions Quietly Degrade Customer Engagement",
        topic: "Customer-Engagement Degradation",
        href: "https://warroomlabs.substack.com/p/operational-interruptions-quietly",
    },
    {
        title: "Introducing Operational Cognition",
        topic: "Operational Cognition",
        href: "https://warroomlabs.substack.com/p/introducing-operational-cognition",
    },
];

// Downloadable/published resources. PDFs are self-hosted copies preserved from the
// previous THG site (originals lived at titanhavengroup.com/s/...).
export const RESOURCES = [
    {
        name: "Daily Shift Command Sheet",
        type: "Free Download — Gumroad",
        text: "A practical frontline shift-control tool from WarRoom Solutions' retail operations work.",
        href: "https://titanhaven.gumroad.com/l/daily-shift-command-sheet",
    },
    {
        name: "Operational Intelligence Brief",
        type: "Published Brief — PDF",
        text: "The WarRoom operational intelligence brief, published through WarRoom Solutions.",
        href: "/assets/resources/WarRoom_Operational_Intelligence_Brief.pdf",
    },
    {
        name: "Conversion & Intent Diagnostic — Case Brief",
        type: "Redacted Case Brief — PDF",
        text: "Redacted commercial diagnostic for a multi-property commerce portfolio.",
        href: "/assets/resources/THG_WarRoom_Redacted_Conversion_Intent_Diagnostic_Case_Study.pdf",
    },
    {
        name: "Strategic Messaging & Governance — Case Brief",
        type: "Confidential Case Brief — PDF",
        text: "Confidential governance engagement: messaging boundaries, decision logic, reusable reference.",
        href: "/assets/resources/THG_Confidential_Strategic_Messaging_Governance_Case_Brief.pdf",
    },
];

export const EXTERNAL_WORK = [
    {
        title: "Commercial Reality Diagnostic",
        label: "Redacted",
        text: "A redacted diagnostic for a multi-property commerce portfolio: rising traffic and engagement alongside declining conversion. The finding was structural — multiple credible destinations and unclear purchase authority were extending customer evaluation instead of letting a buying decision conclude.",
        href: "/assets/resources/THG_WarRoom_Redacted_Conversion_Intent_Diagnostic_Case_Study.pdf",
    },
    {
        title: "Strategic Messaging & Governance Engagement",
        label: "Confidential",
        text: "A completed strategic-governance engagement for a confidential international client: messaging boundaries, structural rules, decision logic, and a reusable governance reference designed to keep future contributors aligned after delivery.",
        href: "/assets/resources/THG_Confidential_Strategic_Messaging_Governance_Case_Brief.pdf",
    },
];

// Institutional capability lanes (THGov / institutional inquiries).
export const CAPABILITIES = [
    "Proposal development",
    "Planning support",
    "Research synthesis",
    "Documentation",
    "Operational analysis",
];

export const SOCIAL = [
    { label: "THG Retail — TikTok", href: "https://www.tiktok.com/@thg_retail" },
    { label: "WarRoom THG — TikTok", href: "https://www.tiktok.com/@warroomthg" },
    { label: "THG Retail — Instagram", href: "https://www.instagram.com/thg_retail" },
    { label: "WarRoom THG — Instagram", href: "https://www.instagram.com/warroomthg" },
];

// Product detail content. gallerySlots reserves image positions; empty slots render
// flagged placeholders until authentic screenshots are supplied (never fabricate).
export const PRODUCT_DETAILS = {
    "warroom-storecentral": {
        intro: "A modular retail operating environment — the connected system behind how stores actually run.",
        gallerySlots: 3,
        gallery: [
            {
                src: "/assets/products/storecentral-today.jpg",
                alt: "StoreCentral Today view — the operating loop and full module set, from Shift Command to Labor Planning",
                caption: "Today — Shift Command",
            },
            {
                src: "/assets/products/storecentral-intelligence.jpg",
                alt: "StoreCentral Performance & Intelligence — KPI actuals, targets, variance and attainment",
                caption: "Performance & Intelligence",
            },
            {
                src: "/assets/products/storecentral-labor.jpg",
                alt: "StoreCentral Labor Planning — demand forecast, labor plan and coverage comparison",
                caption: "Labor Planning",
            },
        ],
        galleryNote: "Authentic captures from the running StoreCentral product (demonstration tenant).",
        research: {
            text: "StoreCentral applies WarRoom research on execution visibility and organizational signal distortion — keeping what changed, what was done, and what still requires action visible between the plan and the reported result.",
            topics: ["Execution Visibility", "KPI Reinforcement Loops"],
        },
        positioning: "The complete retail operating platform. One predictable price. Use what you need.",
        positioningNote:
            "StoreCentral is the management and execution layer around the point-of-sale system. It coordinates store and district work without replacing the system that records sales, payments, customers, and inventory.",
        fit: {
            left: {
                title: "Point of Sale / Transaction Platform",
                items: [
                    "Sales and payments",
                    "Products and inventory",
                    "Customers and loyalty",
                    "Transaction records and reporting",
                ],
            },
            right: {
                title: "WarRoom StoreCentral",
                items: [
                    "Manager and shift execution",
                    "Tasks, communications, and handoffs",
                    "Training and certification",
                    "Scheduling, time and attendance, and labor planning",
                    "Store visits and audits",
                    "Performance, district visibility, and accountability",
                ],
            },
            note: "StoreCentral is designed to operate alongside the retailer's existing point-of-sale and transactional systems. Data connections, migration, and bespoke connectors are scoped with the retailer or technology partner; no specific third-party integration is implied.",
        },
        partners: {
            text: "StoreCentral can extend an existing POS, payments, reseller, or retail-technology relationship into day-to-day operating execution without displacing the underlying transaction platform. Referral, reseller, and appropriately scoped integration relationships are available.",
            cta: { label: "Discuss a Partner Fit", subject: "Retail Technology Partner Inquiry — WarRoom StoreCentral" },
        },
        choice: {
            heading: "StoreCentral or Control Shift?",
            left: {
                title: "WarRoom StoreCentral",
                text: "For multi-location organizations needing a broader operating system across store and district execution, communications, tasks, learning, assurance and audits, labor and scheduling context, history, administration, and operational intelligence.",
            },
            right: {
                title: "Control Shift",
                text: "For teams wanting a focused, fast-to-deploy live-shift command system: opening, priorities, quick capture, floor conditions, interventions, missed demand, KPI checkpoints, persistent actions, and closing handoff.",
                link: { to: "/portfolio/control-shift", label: "See Control Shift" },
            },
            note: "Shift Command is the native operating-day workflow inside StoreCentral's control system. It is not the standalone Control Shift application, and StoreCentral does not embed, call, or depend on Control Shift.",
        },
        pricing: {
            heading: "Commercial Structure",
            lede: "StoreCentral is priced by location and deployment scope, with unlimited standard users and the standard module set included.",
            included: "Pilot, multi-location, partner, and enterprise pricing available.",
            rows: [
                "Onboarding and guided evaluations are scoped to the deployment and confirmed in the written proposal.",
                "Data migration, bespoke connectors, exceptional storage, and unusually high AI usage are separately scoped.",
                "Secure Stripe billing is available to approved customers after tenant provisioning.",
            ],
            ctas: [
                { label: "Request Pricing", subject: "Pricing Request — WarRoom StoreCentral" },
                { label: "Discuss a Deployment", subject: "Deployment Discussion — WarRoom StoreCentral" },
            ],
            note: "Sales-assisted — no public signup or checkout. Written proposals control commercial terms.",
        },
        sections: [
            {
                heading: "One operating environment",
                text: "StoreCentral brings the functions store teams and district leadership rely on into a single connected platform, rather than scattering them across disconnected tools.",
            },
            {
                heading: "What it spans",
                text: "StoreCentral is modular by design — operators adopt the functions they need.",
                modules: [
                    "Manager / shift control",
                    "Communications & knowledge",
                    "Tasks & execution",
                    "Learning & certification",
                    "Workforce scheduling",
                    "Time & attendance",
                    "Labor planning & forecasting",
                    "Store visits & audits",
                    "Performance / KPI visibility",
                    "Multi-store & district visibility",
                ],
            },
            {
                heading: "The same discipline as the research",
                text: "StoreCentral is designed around the operating discipline WarRoom publishes: what happens on the floor should stay visible to the people responsible for it. Shift Command — StoreCentral's native operating-day workflow — carries that discipline through opening, midshift, and handoff without embedding or depending on the separate Control Shift application.",
            },
        ],
        cta: { label: "Request a Demo", subject: "Demo Request — WarRoom StoreCentral" },
    },
    "control-shift": {
        intro: "Real-time operating control for the person running the floor.",
        gallerySlots: 2,
        gallery: [
            {
                src: "/assets/products/control-shift-screenshot.png",
                alt: "Control Shift interface — shift control view with checkpoints and KPI panel",
                caption: "Shift Command — the operating position",
            },
            {
                src: "/assets/products/control-shift-context.png",
                alt: "Control Shift KPI Check and shift checkpoints — numbers recorded with contemporaneous floor context",
                caption: "KPI Check — numbers with context",
            },
        ],
        galleryNote: "Authentic product captures from the Manager Control interface. Screens shown use seeded demonstration data; no customer data is displayed.",
        research: {
            text: "Control Shift operationalizes Goodhart's Law — an established principle THG applies to live operating environments — and the reinforcement-loop research behind it: measures stay connected to the floor conditions that produced them.",
            topics: ["Goodhart's Law in Operating Environments", "KPI Reinforcement Loops"],
        },
        relationshipNote:
            "Control Shift is a separate, independently deployed application for teams that want focused live-shift execution. StoreCentral has its own native Shift Command and manager-control capabilities inside the broader platform; it does not embed or depend on Control Shift. It gives store managers a live view of what changed, what requires action now, what is overdue, and what must be handed off. It is designed to work alongside existing point-of-sale and operating systems.",
        pricing: {
            heading: "Pricing",
            lede: "Simple per-location pricing with no per-user fees.",
            included: "Launch, pilot, partner, and volume pricing are available for multi-location operators.",
            rows: [
                "Unlimited managers and district users. Standard setup included.",
                "Current launch, pilot, and volume terms are provided in a written proposal.",
            ],
            ctas: [
                { label: "Request Current Pricing", subject: "Pricing Request — Control Shift" },
                { label: "Discuss a Pilot", subject: "Pilot / Product Inquiry — Control Shift" },
            ],
            note: "Control Shift is not a StoreCentral tier, module, prerequisite, or runtime dependency. Shift Command is the separate native operating-day workflow inside StoreCentral. Written proposals control commercial terms.",
        },
        sections: [
            {
                heading: "What it does",
                text: "Control Shift gives store managers a structured operating layer for live shifts: what needs attention now, what changed, missed demand, actions taken, and what gets handed off. Performance measures stay connected to the floor conditions that produced them.",
            },
            {
                heading: "Shift checkpoints",
                text: "The product is structured around the operating day, not a retrospective reporting cycle.",
                modules: [
                    "Opening Control — establish the operating position",
                    "Midshift Reset — reassess as conditions change",
                    "KPI Check — connect measures to floor conditions",
                    "Closing Handoff — preserve context for continuity",
                ],
            },
            {
                heading: "Separate from StoreCentral",
                text: "StoreCentral is a separate, broader retail operating platform with its own native Shift Command workflow and manager-control capabilities, extending across additional retail operating functions — scheduling, labor planning, learning, audits, and district visibility. The products address overlapping operating problems but are independent applications: neither embeds, includes, or depends on the other.",
                link: { to: "/portfolio/warroom-storecentral", label: "See StoreCentral" },
            },
            {
                heading: "Limited pilot",
                text: "WarRoom Solutions is opening discussions with retail operators regarding a limited pilot program — deployed in a small number of stores and evaluated under actual operating conditions, not a simulated environment.",
            },
        ],
        cta: { label: "Discuss a Pilot", subject: "Pilot / Product Inquiry — Control Shift" },
    },
    "warroom-ai": {
        intro: "An AI COO for operating founders.",
        gallerySlots: 2,
        gallery: [
            {
                src: "/assets/products/warroom-ai-hero.jpg",
                alt: "WarRoom AI — Sarge, the AI COO for founders: the launched, live product site",
            },
            {
                src: "/assets/products/warroom-ai-capabilities.jpg",
                alt: "What Sarge does — Remembers, Tracks, Surfaces risk, Grounded: persistent operating context, not a generic chatbot",
                wide: true,
            },
        ],
        sections: [
            {
                heading: "Operating purpose",
                text: "WarRoom AI — Sarge — is designed as a COO-level counterpart: it maintains persistent operating context so a founder's situation, constraints, and history carry forward instead of resetting every conversation.",
            },
            {
                heading: "Capabilities",
                modules: [
                    "Persistent operating context & memory",
                    "Command briefs",
                    "Conversational operating support",
                    "Founder decision support",
                ],
            },
            {
                heading: "The principle",
                text: "WarRoom AI is built on THG's standing principle: AI supports human judgment, workflows, interpretation, and execution — it does not replace judgment.",
            },
            {
                heading: "Status",
                text: "Sarge is a functioning, live commercial product. Founding Beta is now open and limited to the first 10 WarRooms, at $149 per month or $1,490 for one year paid upfront. It remains governed by THG's standing principle: AI supports human judgment; it does not replace it.",
            },
        ],
        ctaExternal: { label: "Start Founding Beta", href: "https://sarge.titanhavengroup.com/" },
        ctaSecondary: {
            label: "Book a 15-minute operating walkthrough",
            email: "mm@titanhavengroup.com",
            subject: "Sarge — 15-Minute Operating Walkthrough",
        },
        research: {
            text: "Sarge applies WarRoom's operational-cognition research: how operators recognize, interpret, prioritize and act under real operating constraints — then carries decisions, commitments and outcomes forward.",
            topics: ["Operational Cognition"],
        },
        pricing: {
            heading: "Founding pricing — launch offer",
            cards: [
                { name: "Monthly", price: "$149", unit: "per month" },
                { name: "Annual", price: "$1,490", unit: "one year, paid upfront" },
            ],
            included:
                "Founding Beta is open and limited to the first 10 WarRooms. Each subscription includes one founder, one business workspace, and up to 500 model requests per month. The first five customers receive guided setup and their first Command Brief. A 14-day money-back guarantee applies to the first payment. Founding pricing is locked for the initial 12-month term.",
        },
    },
    hoteltrips: {
        intro: "An operating digital travel property — rebuilt and back in market.",
        gallerySlots: 1,
        gallery: [
            {
                src: "/assets/products/hoteltrips-screenshot.jpg",
                alt: "HotelsTrips.com live site — event-based travel search and hotel planning",
            },
        ],
        sections: [
            {
                heading: "What it does",
                text: "HotelsTrips maps where to stay around events — games, concerts, tournaments — helping travelers find the right areas, the smartest transit, and the right hotels before they ever reach a booking site.",
            },
            {
                heading: "Status",
                text: "Rebuilt and relaunched August 29, 2026. HotelsTrips operates as an active digital-commerce property within the THG portfolio and continues to be developed. The functioning property itself is the evidence.",
            },
        ],
        cta: null,
    },
};

// Portfolio lane detail content (dedicated pages beyond product slugs).
export const LANES = {
    retail: {
        overline: "THG Retail Concepts",
        title: "Physical products and retail concepts.",
        status: "Site Under Reconstruction",
        lede: "THG Retail is an existing Titan Haven Group LLC business lane covering physical products and retail concepts.",
        statusText:
            "THG Retail's new digital storefront is currently under reconstruction — a new customer-facing storefront on Shopify commerce infrastructure. The permanent destination remains thgretail.com; public external routing will activate when the rebuilt site is ready. The business is not new — the website is being rebuilt.",
        brands: [
            // Each brand can accept an `image` field (authentic Shopify catalog capture)
            // when the property is temporarily reactivated — no redesign needed.
            { name: "PBLC", text: "Physical product brand within THG Retail." },
            { name: "Hat Bar", text: "Retail headwear concept." },
            { name: "Headwear", text: "Hats and headwear products." },
            { name: "Apparel", text: "Apparel and related physical-product concepts." },
        ],
        imageryNote:
            "Authentic product imagery will be added from the existing Shopify catalog when the property is temporarily reactivated for review during the rebuild. No fabricated product photography is used.",
    },
    media: {
        overline: "Sports / Media / IP",
        title: "Media and intellectual-property interests.",
        lede: "THG's media/IP lane spans owned properties, affiliated entities, and properties developed within affiliated entities. The relationship is stated on each entry — they are not identical structures.",
    },
};

export const IDENTIFIERS = {
    legalName: "Titan Haven Group LLC",
    structure: "Wyoming limited liability company",
    samUei: "C3A5BRAFA2T7",
    cage: "16T44",
    itq: "Pennsylvania ITQ Approved Vendor — Management & Government Operations Analysis",
};

export const LEADERSHIP = {
    name: "Peter Saad",
    role: "Managing Member",
    org: "Titan Haven Group LLC",
};

export const inquiryMailto = (subject) =>
    `mailto:${CONTACT.email}?subject=${encodeURIComponent(subject)}`;

// Adopted public pricing (Peter-authorized 2026-09-04; matches live billing records
// for Control Shift and Sarge). StoreCentral remains sales-assisted — no checkout.
// Architecture lock: StoreCentral ≠ Control Shift ≠ Shift Command (native StoreCentral
// workflow) ≠ Store Manager Control Kit (digital resource). Never conflate.
export const PRICING = {
    software: [
        {
            name: "WarRoom StoreCentral",
            tag: "The complete retail operating platform",
            status: "Commercial Product",
            to: "/portfolio/warroom-storecentral",
            priceMain: "Per location",
            priceUnit: "priced by deployment scope",
            priceAlt: "Pilot, multi-location, partner, and enterprise pricing available",
            points: [
                "Unlimited users — no per-user charge",
                "Every standard StoreCentral module included — no ordinary module or add-on gating",
                "Onboarding and guided evaluations scoped to the deployment",
                "Data migration, bespoke connectors, exceptional storage, and unusually high AI usage are separately scoped",
                "Secure Stripe billing is available to approved customers after tenant provisioning",
            ],
            cta: { label: "Request Pricing", subject: "Pricing Request — WarRoom StoreCentral" },
            note: "Sales-assisted — no public signup or checkout. Written proposals control commercial terms.",
        },
        {
            name: "Control Shift",
            tag: "Standalone, mobile-first live-shift command for store managers",
            status: "Commercial Product",
            to: "/portfolio/control-shift",
            priceMain: "Per location",
            priceUnit: "no per-user fees",
            priceAlt: "Launch, pilot, partner, and volume pricing available for multi-location operators",
            points: [
                "Unlimited managers and district users",
                "Standard setup included",
                "Pilot and volume structures scoped in a written proposal",
            ],
            cta: { label: "Request Current Pricing", subject: "Pricing Request — Control Shift" },
            note: "A separate, independently deployed application — not a StoreCentral tier, module, prerequisite, or runtime dependency.",
        },
        {
            name: "Sarge — WarRoom AI",
            tag: "AI COO for operating founders",
            status: "Live",
            to: "/portfolio/warroom-ai",
            priceMain: "$149",
            priceUnit: "per month",
            priceAlt: "$1,490 for one year, paid upfront",
            points: [
                "Founding Beta is open and limited to the first 10 WarRooms. The first five customers receive guided setup and their first Command Brief. Includes a 14-day money-back guarantee on the first payment.",
                "One founder, one business workspace, up to 500 model requests per month",
                "Founding price locked for the initial 12-month term",
            ],
            cta: { label: "Request Founding Access", subject: "Pilot / Product Inquiry — WarRoom AI" },
            note: "Founding launch offer — availability is limited.",
        },
    ],
    resources: [
        {
            name: "Store Manager Control Kit",
            tag: "Digital operating resource — not SaaS, and not the Control Shift application",
            tiers: [
                { name: "Individual license", price: "$49.99", note: "For one person — founding price" },
                { name: "Single-store license", price: "$149", note: "For one store/location" },
                { name: "Multi-store license", price: "$299", note: "For use across multiple stores/locations — up to five locations" },
            ],
            cta: { label: "Inquire About the Kit", subject: "Store Manager Control Kit Inquiry" },
        },
    ],
    services: [
        {
            name: "Operational / Retail Performance Diagnostic",
            price: "Engagements start at $7,500",
            to: "/warroom",
        },
        {
            name: "Operating-system design, digital systems & implementation",
            price: "Engagements start at $15,000",
            to: "/warroom",
        },
        {
            name: "Ongoing operational-intelligence / advisory support",
            price: "From $5,000 per month — normally a three-month minimum",
            to: "/warroom",
        },
        {
            name: "Government & applied-research engagements",
            price: "Scoped by statement of work — typical engagements start at $15,000",
            to: "/government-research",
        },
    ],
    partner: {
        title: "For POS, retail-technology, and implementation partners",
        points: [
            "StoreCentral works alongside POS, payroll, HRIS, WFM, and other systems — it is not a POS and does not replace them.",
            "A POS or technology company does not pay for an initial product-fit and API-compatibility review.",
            "Retail customers license StoreCentral.",
            "Any connector or implementation work is scoped only after both parties agree there is a fit.",
            "Referral, reseller, integration, and co-selling structures are available; commercial terms are documented privately after fit review.",
        ],
        cta: { label: "Discuss Partner Pricing", subject: "Retail Technology Partner Inquiry — WarRoom StoreCentral" },
    },
    notes: [
        "A location is one physical retail store operating under the platform.",
        "Taxes and travel are additional where applicable.",
        "Written proposals control if they differ from web pricing.",
        "Final service fees depend on scope, access, travel, data, procurement, and implementation needs.",
    ],
};

// Turf Talk dedicated presentation (/portfolio/turf-talk-2). SportChant, LTD.
// product — affiliated-entity language is deliberate; not a THG-owned WarRoom product.
export const TURF_TALK = {
    slug: "turf-talk-2",
    name: "Turf Talk",
    overline: "Sports / Media / IP · SportChant, LTD.",
    title: "Turf Talk.",
    status: "Closed Beta",
    relationship: "Owned and operated by SportChant, LTD. — designed and built with Titan Haven Group LLC",
    intro: "A football second-screen experience — know what's happening, talk about it with your people, and prove you know the game.",
    galleryWide: {
        src: "/assets/products/turf-talk-2-scoreboard.jpg",
        alt: "Turf Talk scoreboard — final, halftime, delayed, live and upcoming game states, each matchup with its own room (closed-beta demonstration data)",
    },
    phones: [
        {
            src: "/assets/products/turf-talk-phone-home.jpg",
            alt: "Turf Talk phone app — opening screen with the day's game slate, free scoreboard preview",
            caption: "Today's slate",
        },
        {
            src: "/assets/products/turf-talk-phone-slate.jpg",
            alt: "Turf Talk phone app — game slate showing final, halftime, delayed and live states, each with a room",
            caption: "Every game state",
        },
        {
            src: "/assets/products/turf-talk-phone-game.jpg",
            alt: "Turf Talk phone app — matchup view for Oklahoma at Texas with room, stats and prediction-card entry",
            caption: "The matchup view",
        },
    ],
    disclosure:
        "Closed-beta demonstration screens from the running Turf Talk build. Displayed games and data are seeded for demonstration — not a live sports feed.",
    cta: { label: "Request a Demo", subject: "Turf Talk Demo Request" },
    productAddress: { label: "Turf Talk", domain: "turftalk.titanhavengroup.com", href: "https://turftalk.titanhavengroup.com" },
    sections: [
        {
            heading: "What it is",
            text: "Turf Talk is a sports conversation and prediction platform owned and operated by SportChant, LTD., a New York corporation. The rebuilt platform was designed and built with Titan Haven Group LLC as product-development partner. It is built around game day: a scoreboard spanning NFL and college football, a dedicated room for every matchup, picks and predictions, rivalry leagues, and tailgate planning.",
        },
        {
            heading: "What makes it different",
            text: "Turf Talk treats the game as a social event rather than a data feed. Rivalry leagues let groups of friends compete across a season, and every matchup carries its own conversation. Alongside the fan experience sits the Character Show — original SportChant programming in which fictional characters, including Alpha Moose, Cow Pie, and Bruce, deliver voiced, animated game programming around selected matchups. The format is designed to be reusable, not a one-off.",
        },
        {
            heading: "Where it stands",
            text: "Turf Talk is in active development and running as a closed, invite-only beta. The working beta environment currently uses clearly identified demonstration data while the live sports-data connection is completed. The application interior is available only through controlled access — the public product address is a front door, not open access. Guided demonstrations can be requested through Titan Haven Group.",
        },
    ],
};

// Bud's Market Intelligence Desk dedicated presentation (/portfolio/bud).
// WarRoom Labs product — LIVE at bud.titanhavengroup.com; launch product: Bud Basket v2.6
// and live-record architecture. Not investment advice; no established predictive
// edge or graded-performance claims until the public record proves them.
export const BUD = {
    slug: "bud",
    name: "Bud's Market Intelligence Desk",
    overline: "WarRoom Labs",
    title: "Bud's Market Intelligence Desk.",
    status: "Live",
    relationship: "A WarRoom Labs product — Titan Haven Group LLC",
    externalUrl: "https://bud.titanhavengroup.com",
    intro: "Market intelligence that shows its work — and keeps score.",
    sections: [
        {
            heading: "What it is",
            text: "Bud's Market Intelligence Desk is an experimental WarRoom Labs market-intelligence desk, live at bud.titanhavengroup.com. Its launch product is the Bud Basket v2.6: ranked securities research on a defined review cycle, built from live market data with sources attributed where shown. The desk also publishes a Morning Brief and methodology, keeps an archived call record and Scorecard, and includes Bud's Ringer. Earlier directional calls were not adopted for launch and are preserved as historical/experimental research. Members can follow the desk.",
        },
        {
            heading: "A record kept in public",
            text: "Bud's earlier directional calls are preserved as historical/experimental research: published calls were timestamped and locked, then graded under the disclosed method, with wins and losses kept on the public record. The Bud Basket v2.6 is tracked with the same receipts discipline. THG claims no established predictive edge and quotes no performance figures as endorsement. Bud is research and education tooling, not personalized investment advice.",
        },
        {
            heading: "Where it stands",
            text: "Launched September 8, 2026 and live at bud.titanhavengroup.com as an experimental market-intelligence desk. Quotes update live. See the product for available basket snapshots, historical records, and current review/publication status. The methodology is published for scrutiny.",
        },
    ],
};
